import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { Tag, Badge, Popover, BadgeProps } from "antd";
import { computed } from "mobx";
import { translateKey as t } from "../../locales/translation";
import { config } from "../../config/configUi.js";
import _ from "lodash";

interface WebsocketBadgeProps {
  store: {
    statusBar: {
      wsConnected: boolean;
      wsUserDisabled: boolean;
      ofConnected: boolean;
      connectedDepot: string;
    };
    clientsSocket: { toggle: () => void; isUserDisabled: boolean } | null;
  };
}

const EASTER_EGG_CLICKS = 5;
const EASTER_EGG_WINDOW_MS = 2000;

@inject("store")
@observer
class WebsocketBadge extends Component<WebsocketBadgeProps> {
  ofSocketEnabled = false;
  availableSockets = [];
  clickTimestamps: number[] = [];

  handleBadgeClick = () => {
    const now = Date.now();
    this.clickTimestamps = this.clickTimestamps.filter((t) => now - t < EASTER_EGG_WINDOW_MS);
    this.clickTimestamps.push(now);

    if (this.clickTimestamps.length >= EASTER_EGG_CLICKS) {
      this.clickTimestamps = [];
      this.props.store.clientsSocket?.toggle();
    }
  };

  // List the pages where WS are enabled.
  websocketConfig = [
    {
      path: ["/configuration/clients"],
      name: "Clients Socket",
      hide: _.get(config, "websocketConfig.clients", "") ? false : true,
      message: (state) => this.getSubscriptionMessage(state),
      connected: () => this.props.store.statusBar.wsConnected,
    },
    this.ofSocketEnabled && {
      path: window.location.pathname.toLowerCase(),
      name: t("lblOFSocket", "OF Socket"),
      message: (state) => (state ? t("lblConnected", "Connected") : t("lblDisconnected", "Disconnected")),
      connected: () => this.props.store.statusBar.ofConnected,
    },
  ];

  constructor(props) {
    super(props);

    // Check the WS config.
    this.checkWSConfig();
  }

  @computed get ofSocketState() {
    return this.ofSocketEnabled && this.props.store.statusBar.ofConnected;
  }

  @computed get hasConnectedSockets() {
    return this.availableSockets.length > 0;
  }

  @computed get allSocketsOnline() {
    let online = 0;

    this.availableSockets.forEach((sock) => {
      if (sock.connected()) {
        online++;
      }
    });

    if (online === this.availableSockets.length) {
      return "all";
    }

    if (online === 0) {
      return "none";
    }

    return "partly";
  }

  getSubscriptionMessage = (state) => {
    if (this.props.store.statusBar.connectedDepot) {
      return t("lblSubscribedToItem", "Subscribed to {1}", [this.props.store.statusBar.connectedDepot]);
    }

    return state ? t("lblConnected", "Connected") : t("lblDisconnected", "Disconnected");
  };

  checkWSConfig = () => {
    // Get the path.
    let path = window.location.pathname.toLowerCase();

    // Check if path matches config.
    this.websocketConfig.forEach((config) => {
      if ((Array.isArray(config.path) && config.path.includes(path)) || path === config.path) {
        if (!config.hide) {
          this.availableSockets.push(config);
        }
      }
    });
  };

  getWSStatus = () => {
    if (this.props.store.statusBar.wsUserDisabled) {
      return { state: "default", color: "default" };
    }

    if (!this.hasConnectedSockets) {
      return { state: "success", color: "" };
    }

    if (this.allSocketsOnline === "all") {
      return { state: "success", color: "green" };
    } else if (this.allSocketsOnline === "partly") {
      return { state: "warning", color: "orange" };
    } else {
      return { state: "error", color: "red" };
    }
  };

  getPopoverContent = () => {
    const elements = [];

    if (this.hasConnectedSockets) {
      this.availableSockets.forEach((socket, i) => {
        elements.push(
          <div key={i}>
            <Badge
              status={socket.connected() ? "success" : "error"}
              text={
                <span>
                  <b>{socket.name}</b> - {socket.message(socket.connected())}
                </span>
              }
            />
          </div>
        );
      });
    } else {
      elements.push(
        <div key="noConf">
          <p>{t("lblWSNotConfiguredForThisPage", "WS Not Configured For This Page")}</p>
        </div>
      );
    }

    return elements;
  };

  render() {
    return (
      <Popover content={this.getPopoverContent()} trigger="hover">
        <Tag color={this.getWSStatus().color} style={{ height: 24, lineHeight: "22px", cursor: "pointer" }} onClick={this.handleBadgeClick}>
          <Badge status={this.getWSStatus().state as BadgeProps["status"]} text={t("lblSocket", "Socket")} />
        </Tag>
      </Popover>
    );
  }
}

export { WebsocketBadge };
